import React, { useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@klear/ui/components/card'
import { Building2, Globe } from 'lucide-react'
import FormInput from '@klear/ui/form/input'
import FormCombobox from '@klear/ui/form/combobox'
import { ORGANIZATION_SIZES } from '@klear/constants'
import { COUNTRY_CODE_OPTIONS } from '@klear/constants/countries'
import { useFormContext } from 'react-hook-form'

interface OrganizationFormProps {
  isEditing?: boolean
  showIdentifiers?: boolean
  showParentOrganization?: boolean
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({
  isEditing = true,
  showIdentifiers = false,
  showParentOrganization = false
}) => {
  const { register } = useFormContext()

  const organizationSizes = useMemo(
    () =>
      ORGANIZATION_SIZES.map((size) => ({
        value: size,
        label: size
      })),
    []
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organization Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormInput label="Organization Name" name="organization.name" isEditing={isEditing} />
        <FormInput label="Organization Description" name="organization.description" isEditing={isEditing} />
        {showParentOrganization && (
          <FormInput
            label="Parent Organization"
            name="organization.parentOrganization"
            isEditing={isEditing}
            helperText="Optional"
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          <FormCombobox
            label="Size"
            name="organization.size"
            options={organizationSizes}
            valueType="string"
            isEditing={isEditing}
          />
          <FormCombobox
            label="Country"
            name="organization.country"
            options={COUNTRY_CODE_OPTIONS}
            valueType="string"
            isEditing={isEditing}
            placeholder="Select a country"
            required
          />
        </div>
        <FormInput
          label="Website"
          name="organization.website"
          icon={Globe}
          placeholder="https://example.com"
          isEditing={isEditing}
        />
        {showIdentifiers && (
          <>
            <div className="pt-2">
              <div className="border-t border-gray-200" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="DUNS Number"
                name="organization.dunsNumber"
                isEditing={isEditing}
                helperText="9 digits"
              />
              <FormInput
                label="LEI"
                name="organization.lei"
                isEditing={isEditing}
                helperText="ISO 17442 (20 characters)"
              />
              <FormInput
                label="EIN / FEIN"
                name="organization.ein"
                isEditing={isEditing}
                helperText="9 digits (NN-NNNNNNN)"
              />
              <FormInput
                label="VAT Number"
                name="organization.vatNumber"
                isEditing={isEditing}
                helperText="Add country prefix if applicable"
              />
            </div>
          </>
        )}
      </CardContent>
      {!showParentOrganization && <input type="hidden" {...register('organization.parentOrganization')} />}
      {!showIdentifiers && (
        <>
          <input type="hidden" {...register('organization.dunsNumber')} />
          <input type="hidden" {...register('organization.lei')} />
          <input type="hidden" {...register('organization.ein')} />
          <input type="hidden" {...register('organization.vatNumber')} />
        </>
      )}
    </Card>
  )
}

export default OrganizationForm
